import { AccountBalanceWallet } from '@assets/svgs/AccountBalanceWallet'
import { CurrencyDollarSimple } from '@assets/svgs/CurrencyDollarSimple'
import { currencyMask } from '@utils/masks/currencyMask'
import { InfoIcon } from '@assets/svgs/InfoIcon'
import { PreviewCard } from '../PreviewCard'
import { TooltipExplanation } from '@components/shared/TooltipExplanation/inde'
import { TrendingDown } from '@assets/svgs/TrendingDown'
import { TrendingUp } from '@assets/svgs/TrendingUp'

import type { BudgetPreviewsProps } from './types'

const formatCurrencyAsNegative = (value: number) => {
  const absoluteValue = Math.abs(value)
  const formatted = currencyMask(absoluteValue.toFixed(2))
  return `-${formatted}`
}

const formatPercentageAsNegative = (value: number) => {
  const absoluteValue = Math.abs(value)
  return `-${absoluteValue.toFixed(2)}%`.replace('.', ',')
}

const formatDeviationPercentage = (percentageValue: number, valueInCurrency: number) => {
  const abs = Math.abs(percentageValue)
  const formatted = `${abs.toFixed(2)}%`.replace('.', ',')
  return valueInCurrency < 0 ? `-${formatted}` : formatted
}

const CONSUMPTION_WEEK_COLORS = ['#FDE8D1', '#FACFAD', '#F8A77D', '#F58F5C', '#F2793A']
const BUDGET_BORDER = '#7B8794'

export const budgetPreviews = ({ data }: BudgetPreviewsProps) => {
  if (!data) return []

  const budgetAmount = Math.abs(data.budgetAmount) * -1
  const budgetPercentage = Math.abs(data.budgetPercentage) * -1
  const previewAmount = Math.abs(data.previewCurrentAmount) * -1
  const previewPercentage = Math.abs(data.previewCurrentPercentage) * -1

  const deviationValue = previewAmount - budgetAmount
  const deviationPercentage = previewPercentage - budgetPercentage

  const isDeviationNegative = Math.abs(previewAmount) > Math.abs(budgetAmount)

  const week = data.currentPreviewNumber ?? 3
  const previewBorderColor = CONSUMPTION_WEEK_COLORS[week - 1] ?? CONSUMPTION_WEEK_COLORS[2]

  return [
    <PreviewCard
      isBudget
      borderColor={BUDGET_BORDER}
      label="Budget"
      percentageValue={formatPercentageAsNegative(budgetPercentage)}
      value={formatCurrencyAsNegative(budgetAmount)}
      icon={<AccountBalanceWallet />}
      bottomText="Orçamento aprovado"
      infoIcon={
        <TooltipExplanation
          data={[{ label: 'Budget (%) = ', text: '(Orçamento (R$) ÷ Budget de Faturamento (R$)) × 100' }]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />,
    <PreviewCard
      isBudget
      borderColor={previewBorderColor}
      label={!data.currentPreviewNumber ? 'Prévia' : `Prévia ${data.currentPreviewNumber}ª semana`}
      percentageValue={formatPercentageAsNegative(previewPercentage)}
      value={formatCurrencyAsNegative(previewAmount)}
      icon={<CurrencyDollarSimple />}
      bottomText="Previsão Atual"
      infoIcon={
        <TooltipExplanation
          data={[
            { label: 'Prévia (%) = ', text: '(Total Consumo (R$) ÷ Total de Faturamento (R$)) × 100' },
            { label: 'Prévia (R$) = ', text: 'Total Consumo (R$) ' }
          ]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />,
    <PreviewCard
      label="Desvio"
      borderColor={CONSUMPTION_WEEK_COLORS[CONSUMPTION_WEEK_COLORS.length - 1]}
      percentageValue={formatDeviationPercentage(deviationPercentage, deviationValue)}
      value={currencyMask(deviationValue.toFixed(2))}
      icon={isDeviationNegative ? <TrendingUp /> : <TrendingDown />}
      bottomText={isDeviationNegative ? 'Acima do Budget' : 'Abaixo do Budget'}
      isNegative={isDeviationNegative}
      infoIcon={
        <TooltipExplanation
          data={[
            { label: 'Desvio (%) = ', text: 'Prévia (%) - Budget (%)' },
            { label: 'Desvio (R$) = ', text: 'Prévia (R$) - Budget (R$)' }
          ]}
        >
          <InfoIcon />
        </TooltipExplanation>
      }
    />
  ]
}
